import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Tab, Tabs } from "@mui/material";
import {
  DrawerContent,
  DrawerCustom,
  DrawerNavBar,
  Typography,
} from "@platform/design-system";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { BTX_DEPENDENT_RATE_BASIS } from "@platform/feature-flags";
import {
  CoverageTierEnum,
  RateDesignEnum,
  RateUnitEnum,
} from "@platform/openapi-types";
import { type AxiosInstance } from "axios";
import {
  type FC,
  type ReactNode,
  type SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router";

import { RATE_STRUCTURE } from "../../constants";
import { getDefaultFormDataForCoverage } from "../../coverage-defaults";
import {
  useListRateSchedules,
  useReplaceProposalRateSchedules,
  useReplaceRateSchedules,
} from "../../hooks/useRates";
import { BenefitPlanGroup, RateScheduleFormData } from "../../types/models";
import { getPlanHeaderName } from "../../utils/plan-helpers";
import { isCompanionSchedule } from "../../utils/schedule-helpers";

import {
  buildAgeBandedGenderRates,
  buildAgeBandedRates,
  buildAgeBandedTieredRates,
  buildAgeBandedTobaccoRates,
  buildCompanionRateScheduleRates,
  buildCompositeRates,
  buildFlatRates,
  computeRateDesign,
  createRateBuildContext,
  getEmployeeDependentVariantForTierCount,
  mapTierFromBackendValue,
  equalizeEmpSpouseRates,
  inferEmpSpouseRatesDiffer,
  mergeExistingRateValues,
  normalizeAgeBandsForSave,
  normalizeEmpSpouseRatesDifferForSave,
  normalizeUnitForSave,
  rateKey,
  relabeledTiers,
} from "./rate-helpers";
import { mapFromBackendUnit } from "./RatesTab";
import { RateSetsSection } from "./RateSetsSection";
import { RatesDrawerFooter } from "./RatesDrawerFooter";
import { StructureEditorSection } from "./StructureEditorSection";

import type {
  CreateRateScheduleRequest,
  Rate,
  RateSchedule,
} from "../../types";

/** Type for a single rate schedule payload within the request array */
type RateSchedulePayload = CreateRateScheduleRequest["rateSchedules"][number];

/** Derived rate structure info from backend rate design */
type DerivedRateStructure = Pick<
  RateScheduleFormData,
  | "rateStructure"
  | "structureDetail"
  | "coveredBenefitType"
  | "ratePer"
  | "includeDependentRates"
  | "dependentRateType"
  | "ratesBasedOn"
  | "dependentRatesBasedOn"
>;

/**
 * Derives rate structure from backend rate design.
 * Maps rateDesign -> { rateStructure, structureDetail }
 */
function deriveRateStructureFromDesign(
  rateDesign: string,
  rates: Rate[] = [],
): DerivedRateStructure {
  const result: DerivedRateStructure = {};

  switch (rateDesign) {
    case "composite":
      result.rateStructure = RATE_STRUCTURE.TIERED;
      break;
    case "flatRate":
      result.rateStructure = deriveFlatRateStructure(rates, result);
      break;
    case "ageBanded":
      result.rateStructure = RATE_STRUCTURE.AGE_BANDED;
      result.structureDetail = "none";
      deriveRatesBasedOn(rates, result);
      break;
    case "ageBandedTobacco":
      result.rateStructure = RATE_STRUCTURE.AGE_BANDED;
      result.structureDetail = "tobacco";
      deriveRatesBasedOn(rates, result);
      break;
    case "ageBandedGender":
      result.rateStructure = RATE_STRUCTURE.AGE_BANDED;
      result.structureDetail = "gender";
      deriveRatesBasedOn(rates, result);
      break;
    case "ageBandedTiered":
      result.rateStructure = RATE_STRUCTURE.AGE_BANDED;
      result.structureDetail = "tiered";
      deriveRatesBasedOn(rates, result);
      break;
  }

  return result;
}

/**
 * Derives ratesBasedOn, coveredBenefitType, and ratePer from the unit on existing rates.
 * Used by both flat rate and age-banded designs.
 */
const deriveRatesBasedOn = (
  rates: Rate[],
  result: DerivedRateStructure,
): void => {
  const rateUnit = rates[0]?.unit;
  if (!rateUnit) return;

  // If unit is "usd" but volume data exists and no rate has numberOfLives, the
  // unit was stored incorrectly. Correct to volumePer1000 (the standard unit
  // for volume-based life rates). A rate with both volume and numberOfLives is
  // a legitimately unit-based (PB-680) rate with a stray volume value, not a
  // mis-stored one.
  const hasVolumeData = rates.some((r) => r.volume != null);
  const hasNumberOfLivesData = rates.some((r) => r.numberOfLives != null);
  const effectiveUnit =
    rateUnit === RateUnitEnum.usd && hasVolumeData && !hasNumberOfLivesData
      ? RateUnitEnum.volumePer1000
      : rateUnit;

  if (effectiveUnit === "usd") {
    result.ratesBasedOn = "unit";
  } else {
    result.ratesBasedOn = "volume";
    const { coveredBenefitType, ratePer } = mapFromBackendUnit(effectiveUnit);
    result.coveredBenefitType = coveredBenefitType;
    result.ratePer = ratePer;
  }
};

/** Coverage tier codes that indicate a dependent-tier rate row. */
/** Selected-tab text and indicator colour for the drawer's tab bars. */
const TAB_ACCENT = "brandPrimary.main";

const DEPENDENT_TIER_CODES = [
  CoverageTierEnum.employeeDependent,
  CoverageTierEnum.spouseOnly,
  CoverageTierEnum.childOnly,
  CoverageTierEnum.spouseChildren,
];

/**
 * Derives dependentRatesBasedOn from the unit on the first dependent-tier rate
 * found across the given rate sets (primary schedule rates, plus an optional
 * companion child schedule's rates for structures that split children out).
 */
const deriveDependentRatesBasedOn = (
  result: DerivedRateStructure,
  ...rateSets: (Rate[] | undefined)[]
): void => {
  const dependentRate = rateSets
    .flat()
    .find(
      (r): r is Rate =>
        r != null &&
        DEPENDENT_TIER_CODES.includes(r.coverageTierCode as CoverageTierEnum),
    );
  if (!dependentRate?.unit) return;
  result.dependentRatesBasedOn =
    dependentRate.unit === RateUnitEnum.usd ? "unit" : "volume";
};

/**
 * Derives rate structure for flat rate designs (flat vs pepm).
 */
function deriveFlatRateStructure(
  rates: Rate[],
  result: DerivedRateStructure,
): "flat" | "pepm" {
  const hasPepmRates = rates.some(
    (r) =>
      r.unit === RateUnitEnum.totalPepm ||
      r.unit === RateUnitEnum.fmlaPepm ||
      r.unit === RateUnitEnum.adaPepm,
  );

  if (hasPepmRates) {
    const hasGender = rates.some((r) => r.gender != null);
    result.structureDetail = hasGender ? "gender" : "none";
    return "pepm";
  }

  deriveRatesBasedOn(rates, result);

  // Detect dependent rates from coverage tier codes
  const hasDependentTiers = rates.some(
    (r) =>
      r.coverageTierCode === CoverageTierEnum.employeeDependent ||
      r.coverageTierCode === CoverageTierEnum.spouseOnly ||
      r.coverageTierCode === CoverageTierEnum.childOnly,
  );

  if (hasDependentTiers) {
    result.includeDependentRates = true;
    // Separate = has spouseOnly or childOnly; combined = has employeeDependent
    const hasSeparate = rates.some(
      (r) =>
        r.coverageTierCode === CoverageTierEnum.spouseOnly ||
        r.coverageTierCode === CoverageTierEnum.childOnly,
    );
    result.dependentRateType = hasSeparate ? "separate" : "combined";
    deriveDependentRatesBasedOn(result, rates);
  } else {
    result.includeDependentRates = false;
  }

  return "flat";
}

/**
 * Extracts age bands from existing rates.
 */
function extractAgeBandsFromRates(
  rates: Rate[],
): { lower: number; upper: number }[] {
  const ageBands: { lower: number; upper: number }[] = [];
  const seenBands = new Set<string>();

  for (const rate of rates) {
    if (rate.ageBandLower === undefined && rate.ageBandUpper === undefined) {
      continue;
    }
    // nil lower = <N band (restore sentinel lower=0); nil upper = >N band (restore sentinel upper=150)
    const lower = rate.ageBandLower ?? 0;
    const upper = rate.ageBandUpper ?? 150;
    const key = `${lower}-${upper}`;
    if (!seenBands.has(key)) {
      seenBands.add(key);
      ageBands.push({ lower, upper });
    }
  }

  return ageBands;
}

/**
 * Extracts coverage tiers from existing rates.
 * Maps backend values to UI values (e.g., employeeDependent -> employeePlusOneDependent)
 */
function extractTiersFromRates(rates: Rate[]): string[] {
  const backendTiers = rates.reduce<CoverageTierEnum[]>((tiers, rate) => {
    if (
      rate.coverageTierCode !== undefined &&
      !tiers.includes(rate.coverageTierCode)
    ) {
      tiers.push(rate.coverageTierCode);
    }

    return tiers;
  }, []);

  const preferredEmployeeDependentVariant =
    getEmployeeDependentVariantForTierCount(backendTiers.length);

  return backendTiers.map((tier) =>
    mapTierFromBackendValue(tier, preferredEmployeeDependentVariant),
  );
}

/**
 * Initializes form data from an existing rate schedule.
 */
function initializeFormDataFromSchedule(
  existingSchedule: RateSchedule,
): RateScheduleFormData {
  const rates = existingSchedule.rates || [];
  const structureInfo = deriveRateStructureFromDesign(
    existingSchedule.rateDesign,
    rates,
  );

  const firstUnit = rates[0]?.unit;
  const hasVolumeData = rates.some((r) => r.volume != null);
  const hasNumberOfLivesData = rates.some((r) => r.numberOfLives != null);
  const correctedUnit =
    firstUnit === "usd" && hasVolumeData && !hasNumberOfLivesData
      ? RateUnitEnum.volumePer1000
      : firstUnit;

  const formData: RateScheduleFormData = {
    rateDesign: existingSchedule.rateDesign,
    unit: correctedUnit,
    numberOfLives: existingSchedule.numberOfLives,
    pepm: existingSchedule.pepm,
    ...structureInfo,
  };

  const ageBands = extractAgeBandsFromRates(rates);
  if (ageBands.length > 0) {
    formData.ageBandRanges = ageBands;
  }

  const tiers = extractTiersFromRates(rates);
  if (tiers.length > 0) {
    formData.selectedTiers = tiers;
    formData.numberOfTiers = tiers.length;
  }

  // Detect EE/SP split from existing age-banded rates
  if (formData.rateStructure === RATE_STRUCTURE.AGE_BANDED) {
    const hasEmpSpouseSplit =
      rates.some((r) => r.coverageTierCode === CoverageTierEnum.employee) &&
      rates.some((r) => r.coverageTierCode === CoverageTierEnum.spouseOnly);
    if (existingSchedule.empSpouseRatesDiffer) {
      // Persisted directly on the schedule — authoritative, since it can't be
      // reliably recovered from rate values alone (see inferEmpSpouseRatesDiffer).
      formData.empSpouseRatesDiffer = existingSchedule.empSpouseRatesDiffer;
    } else if (hasEmpSpouseSplit) {
      // Legacy schedule saved before the flag was persisted — fall back to inference.
      formData.empSpouseRatesDiffer = inferEmpSpouseRatesDiffer(rates)
        ? "yes"
        : "no";
    } else if (
      formData.structureDetail !== "tiered" &&
      rates.some((r) => r.coverageTierCode === CoverageTierEnum.employee)
    ) {
      // Only EE rates with no SP counterpart — legacy schedule created in
      // "Yes" (rates differ) mode before SP rates were tracked.
      formData.empSpouseRatesDiffer = "yes";
    }
    if (hasEmpSpouseSplit) {
      deriveDependentRatesBasedOn(formData, rates);
    }
  }

  return formData;
}

/**
 * Builds the rate schedule payload for API calls.
 */
function buildRateSchedulePayload(
  formData: RateScheduleFormData,
  rateDesign: RateDesignEnum,
  desiredRates: Partial<Rate>[],
  planType?: string,
): RateSchedulePayload {
  const payload: RateSchedulePayload = {
    rateDesign,
  };

  if (formData.numberOfLives !== undefined) {
    payload.numberOfLives = formData.numberOfLives;
  }

  if (formData.rateCoverageIndicator && isLifeCoverageType(planType)) {
    payload.rateCoverageIndicator =
      formData.rateCoverageIndicator as RateSchedulePayload["rateCoverageIndicator"];
  }

  if (formData.pepm !== undefined) {
    payload.pepm = formData.pepm;
  }

  if (
    formData.rateStructure === RATE_STRUCTURE.AGE_BANDED &&
    formData.includeDependentRates === true &&
    formData.empSpouseRatesDiffer
  ) {
    payload.empSpouseRatesDiffer = formData.empSpouseRatesDiffer;
  }

  if (desiredRates.length > 0) {
    payload.rates = desiredRates as RateSchedulePayload["rates"];
  }

  return payload;
}

// Coverage types that support AD&D secondary rate schedules
const ADND_COVERAGE_TYPES = ["basiclifeadnd", "voluntarylifeadnd"] as const;

/** Check if coverage type is a life product that supports AD&D */
const isLifeCoverageType = (coverageType?: string): boolean =>
  ADND_COVERAGE_TYPES.includes(
    coverageType?.toLowerCase() as (typeof ADND_COVERAGE_TYPES)[number],
  );

/** Check if coverage type is voluntary life */
const isVoluntaryLife = (coverageType?: string): boolean =>
  coverageType?.toLowerCase() === "voluntarylifeadnd";

/**
 * Finds the primary, AD&D, and companion rate schedules from a list.
 * For non-life products, returns the first non-companion schedule as primary.
 */
function findSchedules(
  schedules: RateSchedule[] | undefined,
  isLifeProduct: boolean,
): {
  primary: RateSchedule | undefined;
  adnd: RateSchedule | undefined;
  companionRate: RateSchedule | undefined;
} {
  const companionRate = schedules?.find((s) =>
    isCompanionSchedule(s, schedules),
  );

  if (!isLifeProduct) {
    const primary = schedules?.find((s) => !isCompanionSchedule(s, schedules));
    return { primary, adnd: undefined, companionRate };
  }
  const primary = schedules?.find(
    (s) =>
      s.rateCoverageIndicator !== "adnd" && !isCompanionSchedule(s, schedules),
  );
  const adnd = schedules?.find((s) => s.rateCoverageIndicator === "adnd");
  return { primary, adnd, companionRate };
}

/**
 * Initializes AD&D form fields from an existing AD&D rate schedule.
 */
function initializeAdndFormDataFromSchedule(
  adndSchedule: RateSchedule,
  coverageType?: string,
): Partial<RateScheduleFormData> {
  const rates = adndSchedule.rates || [];
  if (rates.length === 0) {
    return { adndRatesApply: true };
  }

  const adndData: Partial<RateScheduleFormData> = {
    adndRatesApply: true,
  };

  const employeeRate = rates.find(
    (r) =>
      r.coverageTierCode == null ||
      r.coverageTierCode === CoverageTierEnum.employee,
  );
  const combinedDependentRate = rates.find(
    (r) => r.coverageTierCode === CoverageTierEnum.employeeDependent,
  );
  const spouseRate = rates.find(
    (r) => r.coverageTierCode === CoverageTierEnum.spouseOnly,
  );
  const childRate = rates.find(
    (r) => r.coverageTierCode === CoverageTierEnum.childOnly,
  );

  adndData.employeeAdndRate = employeeRate?.rate;
  adndData.dependentAdndRate = combinedDependentRate?.rate;
  adndData.spouseAdndRate = spouseRate?.rate;
  adndData.childAdndRate = childRate?.rate;

  // Determine dependent configuration from rates
  if (rates.length === 1) {
    // Employee-only AD&D
    adndData.dependentAdndRatesApply = false;
  } else {
    adndData.dependentAdndRatesApply = true;

    // Check if combined or separate by looking at coverage tiers
    const hasSeparateRates = rates.some(
      (r) =>
        r.coverageTierCode === CoverageTierEnum.spouseOnly ||
        r.coverageTierCode === CoverageTierEnum.childOnly,
    );

    if (hasSeparateRates || isVoluntaryLife(coverageType)) {
      adndData.dependentAdndType = "separate";
    } else {
      adndData.dependentAdndType = "combined";
    }
  }

  return adndData;
}

/**
 * Builds the AD&D rates array from form data.
 * AD&D rates are always flat rate, no gender/tobacco/tiers/age bands.
 * Unit matches the primary life rate's unit.
 */
function buildAdndRates(
  formData: RateScheduleFormData,
  primaryUnit: RateUnitEnum,
  coverageType?: string,
): Partial<Rate>[] {
  const unit = primaryUnit;
  const rates: Partial<Rate>[] = [];

  // Employee AD&D rate (always present)
  rates.push({ unit, rate: formData.employeeAdndRate ?? 0 });

  if (formData.dependentAdndRatesApply) {
    const depType = isVoluntaryLife(coverageType)
      ? "separate"
      : formData.dependentAdndType || "combined";

    if (depType === "combined") {
      // Single combined dependent rate
      rates.push({
        unit,
        rate: formData.dependentAdndRate ?? 0,
        coverageTierCode: "employeeDependent" as Rate["coverageTierCode"],
      });
    } else {
      // Separate spouse and child rates
      rates.push({
        unit,
        rate: formData.spouseAdndRate ?? 0,
        coverageTierCode: "spouseOnly" as Rate["coverageTierCode"],
      });
      rates.push({
        unit,
        rate: formData.childAdndRate ?? 0,
        coverageTierCode: "childOnly" as Rate["coverageTierCode"],
      });
    }
  }

  return rates;
}

/** Parameters for initializePlanFormData */
interface InitPlanFormDataParams {
  currentPlanId: string;
  existingSchedule: RateSchedule | undefined;
  existingAdndSchedule: RateSchedule | undefined;
  existingChildSchedule: RateSchedule | undefined;
  isLifeProduct: boolean;
  initializeWithDefaultRates: boolean;
  coverageType: string | undefined;
  formDataByPlan: Record<string, RateScheduleFormData>;
  setFormDataByPlan: React.Dispatch<
    React.SetStateAction<Record<string, RateScheduleFormData>>
  >;
  setExistingScheduleIdByPlan: React.Dispatch<
    React.SetStateAction<Record<string, string | undefined>>
  >;
  setExistingAdndScheduleIdByPlan: React.Dispatch<
    React.SetStateAction<Record<string, string | undefined>>
  >;
  setExistingChildScheduleIdByPlan: React.Dispatch<
    React.SetStateAction<Record<string, string | undefined>>
  >;
  setInitializedPlans: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * Initializes form data for a plan from existing schedules.
 * Extracted to reduce component complexity.
 */
function initializePlanFormData({
  currentPlanId,
  existingSchedule,
  existingAdndSchedule,
  existingChildSchedule,
  isLifeProduct,
  coverageType,
  formDataByPlan,
  initializeWithDefaultRates,
  setFormDataByPlan,
  setExistingScheduleIdByPlan,
  setExistingAdndScheduleIdByPlan,
  setExistingChildScheduleIdByPlan,
  setInitializedPlans,
}: InitPlanFormDataParams): void {
  if (existingSchedule) {
    const newFormData = initializeFormDataFromSchedule(existingSchedule);

    // For life products, also initialize AD&D form fields
    if (isLifeProduct && existingAdndSchedule) {
      const adndData = initializeAdndFormDataFromSchedule(
        existingAdndSchedule,
        coverageType,
      );
      Object.assign(newFormData, adndData);
    }

    // If a companion child rate schedule exists, set dependent fields
    if (existingChildSchedule) {
      newFormData.includeDependentRates = true;
      const isSeparate = (existingChildSchedule.rates ?? []).some(
        (r) => r.coverageTierCode === CoverageTierEnum.childOnly,
      );
      newFormData.dependentRateType = isSeparate ? "separate" : "combined";
      if (!newFormData.dependentRatesBasedOn) {
        deriveDependentRatesBasedOn(newFormData, existingChildSchedule.rates);
      }
    }

    setFormDataByPlan((prev) => ({ ...prev, [currentPlanId]: newFormData }));
    setExistingScheduleIdByPlan((prev) => ({
      ...prev,
      [currentPlanId]: existingSchedule.id,
    }));
    setExistingChildScheduleIdByPlan((prev) => ({
      ...prev,
      [currentPlanId]: existingChildSchedule?.id,
    }));
    if (isLifeProduct) {
      setExistingAdndScheduleIdByPlan((prev) => ({
        ...prev,
        [currentPlanId]: existingAdndSchedule?.id,
      }));
    }
    setInitializedPlans((prev) => new Set(prev).add(currentPlanId));
  } else if (!formDataByPlan[currentPlanId]) {
    setFormDataByPlan((prev) => ({
      ...prev,
      [currentPlanId]: initializeWithDefaultRates
        ? getDefaultFormDataForCoverage(coverageType)
        : {},
    }));

    setExistingScheduleIdByPlan((prev) => ({
      ...prev,
      [currentPlanId]: undefined,
    }));
    setExistingChildScheduleIdByPlan((prev) => ({
      ...prev,
      [currentPlanId]: undefined,
    }));
    if (isLifeProduct) {
      setExistingAdndScheduleIdByPlan((prev) => ({
        ...prev,
        [currentPlanId]: undefined,
      }));
    }
    setInitializedPlans((prev) => new Set(prev).add(currentPlanId));
  }
}

/**
 * Creates a setter for form data scoped to a specific plan ID.
 * Extracted to reduce component complexity.
 */
function createPlanFormDataSetter(
  currentPlanId: string | undefined,
  setFormDataByPlan: React.Dispatch<
    React.SetStateAction<Record<string, RateScheduleFormData>>
  >,
): (
  updater:
    | RateScheduleFormData
    | ((prev: RateScheduleFormData) => RateScheduleFormData),
) => void {
  return (updater) => {
    if (!currentPlanId) return;
    setFormDataByPlan((prev) => ({
      ...prev,
      [currentPlanId]:
        typeof updater === "function"
          ? updater(prev[currentPlanId] ?? {})
          : updater,
    }));
  };
}

export interface RatesDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children?: ReactNode;
  rateId?: string;
  planGroupNickname?: string;
  detailsContent?: ReactNode;
  ratesContent?: ReactNode;
  axiosInstance?: AxiosInstance;
  initializeWithDefaultRates?: boolean;
  /** @deprecated Use benefitPlanGroup instead */
  benefitPlanId?: string;
  /** Benefit plan group containing benefit plans to edit */
  benefitPlanGroup?: BenefitPlanGroup;
  /** Benefit plan ID to select when the drawer opens */
  initialBenefitPlanId?: string;
  /** Callback fired after successful save to allow parent to refresh data */
  onSaveSuccess?: () => void;
  /**
   * Plan type determines which replace endpoint is called on save.
   * - 'benefit' (default): PUT /btx/v1/rate-schedules?benefitPlanId=
   * - 'proposal': PUT /btx/v1/proposal-rate-schedules?proposalPlanId=
   *   Also disables fetching from the regular rate-schedules endpoint; the
   *   parent must seed the cache (e.g. via useResolvedRateSchedules).
   */
  planType?: "benefit" | "proposal";
  /**
   * When true, shows the Current / Renewal / Revised Renewal rate sets for this
   * plan. Enabled only for the Incumbent/Current plan on the Quotes SBS grid;
   * omitted elsewhere (e.g. carrier plans, the Build tab) so no renewal UI shows.
   */
  enableRateSets?: boolean;
  /**
   * Coverage type (e.g. "basicLifeAdnd"), used to drive AD&D and other
   * LOC-specific rate-structure UI. Falls back to the `:planName` route param
   * (the Build tab's URL segment) when omitted, so only callers without that
   * route param — e.g. the Quotes tab — need to pass this explicitly.
   */
  coverageType?: string;
}

function getInitialActivePlanIndex(
  benefitPlanGroup: BenefitPlanGroup | undefined,
  initialBenefitPlanId: string | undefined,
): number {
  if (!benefitPlanGroup?.benefitPlans.length || !initialBenefitPlanId) {
    return 0;
  }

  const matchedPlanIndex = benefitPlanGroup.benefitPlans.findIndex(
    (plan) => plan.id === initialBenefitPlanId,
  );

  return matchedPlanIndex >= 0 ? matchedPlanIndex : 0;
}

/**
 * Normalizes AD&D form fields — clears all AD&D fields when AD&D is not applied.
 */
function normalizeAdndFields(
  formData: RateScheduleFormData,
): RateScheduleFormData {
  if (formData.adndRatesApply) return formData;
  return {
    ...formData,
    adndRatesApply: false,
    employeeAdndRate: undefined,
    dependentAdndRatesApply: false,
    dependentAdndRate: undefined,
    spouseAdndRate: undefined,
    childAdndRate: undefined,
    dependentAdndType: undefined,
  };
}

/** Rate designs that support an AD&D secondary schedule */
const ADND_SUPPORTED_DESIGNS: RateDesignEnum[] = [
  RateDesignEnum.flatRate,
  RateDesignEnum.ageBanded,
  RateDesignEnum.ageBandedTobacco,
  RateDesignEnum.ageBandedGender,
  RateDesignEnum.ageBandedTiered,
];

/** Parameters for buildAdndSchedulePayload */
interface BuildAdndParams {
  normalizedFormData: RateScheduleFormData;
  rateDesign: RateDesignEnum;
  coverageType: string | undefined;
  /** Saved AD&D rates whose lives and volume carry over; empty when the rate design changed */
  existingAdndRates: Rate[];
}

interface BuildRateSchedulesForSaveParams {
  formData: RateScheduleFormData;
  coverageType?: string;
  isLifeProduct: boolean;
  planType?: string;
  /** The saved primary schedule; its values carry over while its rate design is kept */
  existingPrimarySchedule?: Pick<RateSchedule, "rateDesign" | "rates">;
  /** Rates on the saved companion (dependent) schedule */
  existingCompanionRates?: Rate[];
  /** Rates on the saved AD&D schedule */
  existingAdndRates?: Rate[];
  /** Gates the Basic Life EE/dependent rate basis divergence behind BTX_DEPENDENT_RATE_BASIS */
  isDependentRateBasisEnabled: boolean;
}

/**
 * Builds an AD&D schedule payload if applicable (flat or age-banded life product with AD&D enabled).
 * Returns undefined if AD&D is not needed.
 */
function buildAdndSchedulePayload({
  normalizedFormData,
  rateDesign,
  coverageType,
  existingAdndRates,
}: BuildAdndParams): RateSchedulePayload | undefined {
  if (
    !normalizedFormData.adndRatesApply ||
    !ADND_SUPPORTED_DESIGNS.includes(rateDesign)
  ) {
    return undefined;
  }

  const primaryUnit =
    (normalizedFormData.unit as unknown as RateUnitEnum) || RateUnitEnum.usd;
  const adndRates = mergeExistingRateValues(
    buildAdndRates(normalizedFormData, primaryUnit, coverageType),
    existingAdndRates,
  );

  return {
    rateDesign: RateDesignEnum.flatRate,
    rateCoverageIndicator:
      "adnd" as RateSchedulePayload["rateCoverageIndicator"],
    rates: adndRates as RateSchedulePayload["rates"],
  };
}

/** Designs whose coverage tiers the analyst selects slot by slot. */
const TIER_SELECTION_DESIGNS: ReadonlySet<RateDesignEnum> = new Set([
  RateDesignEnum.composite,
  RateDesignEnum.ageBandedTiered,
]);

export function buildRateSchedulesForSave({
  formData,
  coverageType,
  isLifeProduct,
  planType,
  existingPrimarySchedule,
  existingCompanionRates = [],
  existingAdndRates = [],
  isDependentRateBasisEnabled,
}: BuildRateSchedulesForSaveParams): RateSchedulePayload[] | undefined {
  let normalizedFormData = normalizeAgeBandsForSave(formData);
  normalizedFormData = normalizeUnitForSave(normalizedFormData);
  normalizedFormData = normalizeAdndFields(normalizedFormData);
  normalizedFormData = normalizeEmpSpouseRatesDifferForSave(normalizedFormData);

  const rateDesign = computeRateDesign(normalizedFormData);
  if (!rateDesign) {
    return undefined;
  }

  let desiredRates = buildRatesFromFormData(
    normalizedFormData,
    rateDesign,
    coverageType,
    isDependentRateBasisEnabled,
  );

  // A config edit that keeps the rate design keeps the rating data of every
  // slot it leaves in place; only a change of design starts over from zero.
  const isRateDesignKept = existingPrimarySchedule?.rateDesign === rateDesign;
  if (isRateDesignKept) {
    const existingRates = existingPrimarySchedule.rates ?? [];
    desiredRates = mergeExistingRateValues(
      desiredRates,
      existingRates,
      TIER_SELECTION_DESIGNS.has(rateDesign)
        ? relabeledTiers(desiredRates, existingRates)
        : undefined,
    );
    if (
      normalizedFormData.rateStructure === RATE_STRUCTURE.AGE_BANDED &&
      normalizedFormData.empSpouseRatesDiffer === "no"
    ) {
      desiredRates = equalizeEmpSpouseRates(desiredRates);
    }
  }

  const primaryFormData = isLifeProduct
    ? { ...normalizedFormData, rateCoverageIndicator: "life" }
    : normalizedFormData;

  const rateSchedules: RateSchedulePayload[] = [
    buildRateSchedulePayload(
      primaryFormData,
      rateDesign,
      desiredRates,
      planType,
    ),
  ];

  if (isLifeProduct) {
    const adndPayload = buildAdndSchedulePayload({
      normalizedFormData,
      rateDesign,
      coverageType,
      existingAdndRates: isRateDesignKept ? existingAdndRates : [],
    });
    if (adndPayload) {
      rateSchedules.push(adndPayload);
    }
  }

  const childRateContext = createRateBuildContext(
    normalizedFormData,
    coverageType,
    isDependentRateBasisEnabled,
  );
  const newChildRates = buildCompanionRateScheduleRates(childRateContext);
  const childRates = isRateDesignKept
    ? mergeExistingRateValues(newChildRates, existingCompanionRates)
    : newChildRates;
  if (childRates.length > 0) {
    rateSchedules.push({
      rateDesign: RateDesignEnum.flatRate,
      rates: childRates as RateSchedulePayload["rates"],
    });
  }

  return rateSchedules;
}

/**
 * Base RatesDrawer component (MUI Drawer) for rate details.
 * Controlled via `open` and `onClose`. Content is provided via children.
 *
 * If a rate schedule already exists for the benefitPlanId, updates it.
 * Otherwise, creates a new one.
 */
const scheduleKey = (
  schedule: Pick<RateSchedulePayload, "rateDesign" | "rates"> & {
    rateCoverageIndicator?: string | null;
    empSpouseRatesDiffer?: string | null;
  },
): string =>
  [
    schedule.rateCoverageIndicator ?? "",
    schedule.empSpouseRatesDiffer ?? "",
    schedule.rateDesign ?? "",
    (schedule.rates ?? []).map(rateKey).sort().join(","),
  ].join("::");

const toScheduleKeys = (
  schedules: (Pick<RateSchedulePayload, "rateDesign" | "rates"> & {
    rateCoverageIndicator?: string | null;
    empSpouseRatesDiffer?: string | null;
  })[],
): string =>
  schedules
    .map((s) =>
      scheduleKey({
        rateCoverageIndicator: s.rateCoverageIndicator,
        empSpouseRatesDiffer: s.empSpouseRatesDiffer,
        rateDesign: s.rateDesign,
        rates: s.rates ?? [],
      }),
    )
    .sort()
    .join("||");

export const RatesDrawer: FC<RatesDrawerProps> = ({
  open,
  onClose,

  children: _children,

  rateId: _rateId,
  planGroupNickname,

  detailsContent: _detailsContent,

  ratesContent: _ratesContent,
  axiosInstance,
  benefitPlanId,
  benefitPlanGroup,
  initialBenefitPlanId,
  onSaveSuccess,
  planType = "benefit",
  enableRateSets = false,
  initializeWithDefaultRates = false,
  coverageType: coverageTypeProp,
}) => {
  const isProposalPlan = planType === "proposal";

  // Falls back to the URL param the Build tab's route provides
  // (/rfps/:id/workbench-build-2/forms/:planName) when the caller doesn't
  // pass coverageType explicitly — the Quotes tab has no such route param,
  // so it must pass coverageType itself.
  const { planName: coverageTypeFromRoute } = useParams<{
    planName?: string;
  }>();
  const coverageType = coverageTypeProp ?? coverageTypeFromRoute;
  const isDependentRateBasisEnabled = useFeatureIsOn(BTX_DEPENDENT_RATE_BASIS);

  // Form data is now keyed by benefit plan ID
  const [formDataByPlan, setFormDataByPlan] = useState<
    Record<string, RateScheduleFormData>
  >({});
  // Track which plans have been initialized to prevent re-initialization on re-renders
  const [initializedPlans, setInitializedPlans] = useState<Set<string>>(
    new Set(),
  );
  // Store existing schedule IDs when initialized, keyed by plan ID
  // This ensures we have the correct ID for delete even if query data changes
  const [existingScheduleIdByPlan, setExistingScheduleIdByPlan] = useState<
    Record<string, string | undefined>
  >({});
  // Store existing AD&D schedule IDs separately (for life products)
  const [existingAdndScheduleIdByPlan, setExistingAdndScheduleIdByPlan] =
    useState<Record<string, string | undefined>>({});
  // Store existing child rate companion schedule IDs (for age-banded with separate deps)
  const [existingChildScheduleIdByPlan, setExistingChildScheduleIdByPlan] =
    useState<Record<string, string | undefined>>({});
  // Track active sub-tab (benefit plan) within the Rates tab
  const [activePlanIndex, setActivePlanIndex] = useState(0);

  const replaceSchedules = useReplaceRateSchedules(
    axiosInstance as AxiosInstance,
  );
  const replaceProposalSchedules = useReplaceProposalRateSchedules(
    axiosInstance as AxiosInstance,
  );

  // Reset drawer state
  useEffect(() => {
    if (open) {
      setActivePlanIndex(
        getInitialActivePlanIndex(benefitPlanGroup, initialBenefitPlanId),
      );
    } else {
      // Clear initialization tracking when drawer closes
      // so form data is re-initialized when drawer reopens
      setInitializedPlans(new Set());
      setExistingScheduleIdByPlan({});
      setExistingAdndScheduleIdByPlan({});
      setExistingChildScheduleIdByPlan({});
    }
  }, [open, benefitPlanGroup, initialBenefitPlanId]);

  // Get benefit plans from group, or fall back to legacy single plan
  const benefitPlans = benefitPlanGroup?.benefitPlans ?? [];
  const currentPlan = benefitPlans[activePlanIndex];
  const currentPlanId = currentPlan?.id ?? benefitPlanId;

  // Derive nickname from group or prop. Use `||` so an empty-string nickname
  // (not just a missing one) falls back to the default title.
  const displayNickname =
    benefitPlanGroup?.nickname || planGroupNickname || "Plan Group";

  // Fetch existing schedules for the current benefit plan
  const {
    data: existingSchedules,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isLoading: _isLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: _error,
  } = useListRateSchedules(
    currentPlanId ?? "",
    axiosInstance as AxiosInstance,
    {
      enabled: !!currentPlanId && !!axiosInstance && open && !isProposalPlan,
    },
  );

  // For life products, find primary (life) and AD&D schedules separately.
  // Also finds companion child rate flat schedule if present.
  const isLifeProduct = isLifeCoverageType(coverageType);
  const {
    primary: existingSchedule,
    adnd: existingAdndSchedule,
    companionRate: existingChildSchedule,
  } = findSchedules(existingSchedules, isLifeProduct);

  // Get form data for current plan
  const formData = formDataByPlan[currentPlanId ?? ""] ?? {};
  const setFormData = createPlanFormDataSetter(
    currentPlanId,
    setFormDataByPlan,
  );

  // Initialize form data from existing schedule when available (only once per plan)
  useEffect(() => {
    if (!currentPlanId || initializedPlans.has(currentPlanId)) return;

    initializePlanFormData({
      currentPlanId,
      existingSchedule,
      existingAdndSchedule,
      existingChildSchedule,
      isLifeProduct,
      coverageType,
      formDataByPlan,
      setFormDataByPlan,
      setExistingScheduleIdByPlan,
      setExistingAdndScheduleIdByPlan,
      setExistingChildScheduleIdByPlan,
      setInitializedPlans,
      initializeWithDefaultRates,
    });
  }, [
    existingSchedule,
    existingAdndSchedule,
    existingChildSchedule,
    currentPlanId,
    initializedPlans,
    formDataByPlan,
    isLifeProduct,
    coverageType,
    initializeWithDefaultRates,
  ]);

  const handlePlanTabChange = (
    _event: SyntheticEvent,
    newValue: number,
  ): void => {
    setActivePlanIndex(newValue);
  };

  const handleSave = async (formData: RateScheduleFormData): Promise<void> => {
    if (!currentPlanId) {
      onClose();
      return;
    }

    const rateSchedules = buildRateSchedulesForSave({
      formData,
      coverageType,
      isLifeProduct,
      planType: currentPlan?.type,
      existingPrimarySchedule: existingSchedule,
      existingCompanionRates: existingChildSchedule?.rates,
      existingAdndRates: existingAdndSchedule?.rates,
      isDependentRateBasisEnabled,
    });
    if (!rateSchedules) {
      return;
    }

    const hasExistingSchedules =
      Boolean(existingSchedules?.length) ||
      Boolean(existingScheduleIdByPlan[currentPlanId]) ||
      Boolean(existingAdndScheduleIdByPlan[currentPlanId]) ||
      Boolean(existingChildScheduleIdByPlan[currentPlanId]);

    // Guard: skip delete+recreate if the full rate schedule structure hasn't changed.
    // The replace would only rewrite the same slots with the same values.
    const existingScheduleKeys = toScheduleKeys(existingSchedules ?? []);
    const desiredScheduleKeys = toScheduleKeys(rateSchedules);
    if (
      hasExistingSchedules &&
      rateSchedules.length === (existingSchedules?.length ?? 0) &&
      existingScheduleKeys === desiredScheduleKeys
    ) {
      onClose();
      return;
    }

    try {
      if (isProposalPlan) {
        await replaceProposalSchedules.mutateAsync({
          proposalPlanId: currentPlanId,
          rateSchedules,
        });
      } else {
        await replaceSchedules.mutateAsync({
          benefitPlanId: currentPlanId,
          rateSchedules,
        });
      }

      onSaveSuccess?.();
      onClose();
    } catch {
      // errors surfaced via useRatesErrorHandler in the mutation hooks
    }
  };

  return (
    <DrawerCustom anchor="right" open={open} onClose={onClose} size="lg">
      <DrawerNavBar>
        <Typography variant="subtitle2" color="textSecondary" sx={{ flex: 1 }}>
          {displayNickname}
        </Typography>
        <IconButton aria-label="Close drawer" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DrawerNavBar>
      <Tabs
        value={"rates"}
        aria-label="Rates drawer tabs"
        textColor="secondary"
        indicatorColor="secondary"
        sx={{
          px: 2,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTabs-indicator": {
            height: 2,
            backgroundColor: TAB_ACCENT,
          },
          "& .MuiTab-root.Mui-selected": { color: TAB_ACCENT },
        }}
      >
        <Tab label="Rates" value="rates" />
      </Tabs>
      {/* Sub-tabs for benefit plans within the group */}
      {benefitPlans.length > 0 && (
        <Tabs
          value={activePlanIndex}
          onChange={handlePlanTabChange}
          aria-label="Benefit plan tabs"
          textColor="secondary"
          indicatorColor="secondary"
          sx={{
            px: 2,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "grey.50",
            minHeight: 40,
            "& .MuiTabs-indicator": {
              height: 2,
              backgroundColor: TAB_ACCENT,
            },
            "& .MuiTab-root": { minHeight: 40, py: 1 },
            "& .MuiTab-root.Mui-selected": { color: TAB_ACCENT },
          }}
        >
          {benefitPlans.map((plan, index) => (
            <Tab
              key={plan.id}
              label={getPlanHeaderName(plan) || `Plan ${index + 1}`}
              value={index}
            />
          ))}
        </Tabs>
      )}
      <DrawerContent>
        <StructureEditorSection
          visible={!enableRateSets}
          formData={formData}
          setFormData={setFormData}
          coverageType={coverageType}
        />
        <RateSetsSection
          enabled={enableRateSets}
          benefitPlanId={currentPlanId}
          axiosInstance={axiosInstance}
          onSaved={() => onSaveSuccess?.()}
        />
      </DrawerContent>
      <DrawerNavBar bottom>
        <RatesDrawerFooter
          showSave={!enableRateSets}
          saveDisabled={!formData.rateStructure}
          onSave={() => handleSave(formData)}
          onClose={onClose}
        />
      </DrawerNavBar>
    </DrawerCustom>
  );
};

/**
 * Build rates array from form data based on the rate design.
 * This generates the structure of rates with stubbed rate values (0).
 * Actual rate/volume values can be edited in the RatesGrid after saving.
 */
function buildRatesFromFormData(
  formData: RateScheduleFormData,
  rateDesign: RateDesignEnum | string,
  coverageType?: string,
  isDependentRateBasisEnabled = false,
): Partial<Rate>[] {
  const context = createRateBuildContext(
    formData,
    coverageType,
    isDependentRateBasisEnabled,
  );

  switch (rateDesign) {
    case "flatRate":
    case RateDesignEnum.flatRate:
      return buildFlatRates(context);
    case "ageBanded":
    case RateDesignEnum.ageBanded:
      return buildAgeBandedRates(context);
    case "ageBandedTobacco":
    case RateDesignEnum.ageBandedTobacco:
      return buildAgeBandedTobaccoRates(context);
    case "ageBandedGender":
    case RateDesignEnum.ageBandedGender:
      return buildAgeBandedGenderRates(context);
    case "ageBandedTiered":
    case RateDesignEnum.ageBandedTiered:
      return buildAgeBandedTieredRates(context);
    case "composite":
    case RateDesignEnum.composite:
      return buildCompositeRates(context);
    default:
      return [{ unit: context.unit, rate: 0 }];
  }
}

export default RatesDrawer;
